/**
 * 学员端通用壳与基础组件
 * -----------------------------------------------------------------------------
 * 视觉口径 = 设计稿 `报名与学员管理系统 · UI 设计稿`（390 × 844）。
 * 这里把设计稿里反复出现的形态固化成可复用件：导航栏 / 底部浮动胶囊导航 /
 * 卡片 / 键值行 / 可点行 / 状态胶囊 / 提示条 / 进度轨 / 步骤点 / 按钮 / 空态。
 *
 * 约定：
 *   - 颜色、间距、圆角一律走 `styles/global.css` 的 CSS 变量，组件里不写色值
 *   - 这里只做「怎么长得像」，不含任何业务判定（判定在各页与 `logic.ts`）
 */
import {
  COURSE_STATUS_LABEL,
  type CourseStatus,
  REGISTRATION_STATUS_LABEL,
  type RegistrationStatus,
} from '@enroll/shared';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useInboxUnreadCount } from '../api/hooks';
import {
  AlertIcon,
  BellIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HomeIcon,
  InfoIcon,
  MessageIcon,
  SearchIcon,
  UserIcon,
} from './icons';
import { openImagePreview } from './zoom';

/* ============================================================================
 * 按钮
 * ========================================================================== */
export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  variant?: 'primary' | 'ghost' | 'soft' | 'text';
  size?: 'md' | 'sm' | 'mini';
  block?: boolean;
  loading?: boolean;
  className?: string;
}

/** 统一按钮：主色胶囊 / 白底描边 / 浅蓝实底 / 纯文字 */
export function Button({
  variant = 'primary',
  size = 'md',
  block = false,
  loading = false,
  className,
  children,
  disabled,
  type = 'button',
  ...rest
}: ButtonProps) {
  const classes = [
    'btn',
    `btn--${variant}`,
    size === 'sm' ? 'btn--sm' : size === 'mini' ? 'btn--mini' : '',
    block ? 'btn--block' : '',
    loading ? 'btn--loading' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <span className="btn__spinner" /> : null}
      {children}
    </button>
  );
}

/* ============================================================================
 * 图标按钮
 * ========================================================================== */
export function IconButton({
  label,
  dot = false,
  onClick,
  children,
}: {
  label: string;
  dot?: boolean;
  onClick?: () => void;
  children: ReactNode;
}) {
  return (
    <button type="button" className="icon-button" aria-label={label} onClick={onClick}>
      {children}
      {dot ? <span className="icon-button__dot" /> : null}
    </button>
  );
}

/* ============================================================================
 * 底部导航（浮动胶囊，选中项为实底主色胶囊）
 * ========================================================================== */
const TAB_ITEMS = [
  {
    key: '/home',
    title: '首页',
    icon: (active: boolean) => <HomeIcon size={18} strokeWidth={active ? 2.1 : 1.8} />,
  },
  {
    key: '/inbox',
    title: '消息',
    icon: (active: boolean) => <MessageIcon size={18} strokeWidth={active ? 2.1 : 1.8} />,
  },
  {
    key: '/me',
    title: '我的',
    icon: (active: boolean) => <UserIcon size={18} strokeWidth={active ? 2.1 : 1.8} />,
  },
] as const;

/** 底部导航三项（首页 / 消息 / 我的，三页都有 —— 口径 C-14） */
export function AppTabBar() {
  const navigate = useNavigate();
  const location = useLocation();
  const unread = useInboxUnreadCount();
  const activeKey =
    TAB_ITEMS.find(
      (item) => location.pathname === item.key || location.pathname.startsWith(`${item.key}/`),
    )?.key ?? '/home';

  return (
    <nav className="tabbar" aria-label="主导航">
      <div className="tabbar__pill">
        {TAB_ITEMS.map((item) => {
          const active = item.key === activeKey;
          const badge = item.key === '/inbox' ? (unread.data ?? 0) : 0;
          return (
            <button
              key={item.key}
              type="button"
              className={`tabbar__item${active ? ' tabbar__item--active' : ''}`}
              aria-current={active ? 'page' : undefined}
              onClick={() => navigate(item.key)}
            >
              <span className="tabbar__glyph">
                {item.icon(active)}
                {badge > 0 ? (
                  <span className="tabbar__badge">{badge > 99 ? '99+' : badge}</span>
                ) : null}
              </span>
              <span className="tabbar__label">{item.title}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
}

/* ============================================================================
 * 页面壳
 * ========================================================================== */
/** 内容区的逐页排版档位（对应设计稿各页 Content 的 pad / gap 实测值） */
export type PageLayout = 'home' | 'guide' | 'result' | 'detail' | 'form' | 'tab';

export interface PageShellProps {
  title?: string;
  /** 是否显示返回箭头 */
  back?: boolean;
  /** 自定义返回行为（默认回上一页，无历史时回服务台） */
  onBack?: () => void;
  right?: ReactNode;
  children: ReactNode;
  /** 内容区排版档位：不传则用通用 14 间距 + 0 上留白 */
  layout?: PageLayout;
  /** 底部固定操作区 */
  footer?: ReactNode;
  /** 页脚占几排按钮（1 排 ≈90 高的留白，2 排 ≈150，3 排 ≈200），默认 1 */
  footerRows?: 1 | 2 | 3;
  /** 底部操作区去掉白底（内容本身已是卡片流时用） */
  footerBare?: boolean;
  /** 是否挂底部导航（首页 / 消息 / 我的为 true） */
  tabbar?: boolean;
  /** 导航栏是否用实底（表格类页面滚动时更稳） */
  solidNav?: boolean;
}

export function PageShell({
  title,
  back = false,
  onBack,
  right,
  children,
  layout,
  footer,
  footerRows = 1,
  footerBare = false,
  tabbar = false,
  solidNav = false,
}: PageShellProps) {
  const navigate = useNavigate();

  const goBack = () => {
    if (onBack) {
      onBack();
      return;
    }
    // 转发链接直达时没有可回退的历史栈，兜底回服务台而不是白屏
    if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  const pageClass = [
    'page',
    footer ? 'page--with-footer' : '',
    footer && footerRows === 2 ? 'page--with-footer-2' : '',
    footer && footerRows === 3 ? 'page--with-footer-3' : '',
    tabbar ? 'page--with-tabbar' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const bodyClass = ['page__body', layout ? `page__body--${layout}` : ''].filter(Boolean).join(' ');

  return (
    <div className={pageClass}>
      {title !== undefined ? (
        <header className={`navbar${solidNav ? ' navbar--solid' : ''}`}>
          {back ? (
            <button type="button" className="navbar__back" aria-label="返回" onClick={goBack}>
              <ChevronLeftIcon size={20} />
            </button>
          ) : (
            <span className="navbar__placeholder" />
          )}
          <h1 className="navbar__title">{title}</h1>
          {right ? (
            <div className="navbar__right">{right}</div>
          ) : (
            <span className="navbar__placeholder" />
          )}
        </header>
      ) : null}
      <main className={bodyClass}>{children}</main>
      {footer ? (
        <div className={`page-footer${footerBare ? ' page-footer--bare' : ''}`}>{footer}</div>
      ) : null}
      {tabbar ? <AppTabBar /> : null}
    </div>
  );
}

/* ============================================================================
 * 卡片 / 键值行 / 可点行
 * ========================================================================== */
export function Card({
  title,
  hint,
  action,
  children,
  tight = false,
  className,
}: {
  title?: ReactNode;
  hint?: ReactNode;
  action?: ReactNode;
  children: ReactNode;
  tight?: boolean;
  className?: string;
}) {
  return (
    <section className={`card${tight ? ' card--tight' : ''}${className ? ` ${className}` : ''}`}>
      {title !== undefined || hint !== undefined ? (
        <div className="card__head">
          <span className="card__title">{title}</span>
          {action ?? (hint !== undefined ? <span className="card__hint">{hint}</span> : null)}
        </div>
      ) : null}
      {children}
    </section>
  );
}

/** 键值行（左侧灰标签、右侧深色值，右对齐） */
export function KV({
  label,
  value,
  strong = false,
  soft = false,
  lined = false,
  mono = false,
}: {
  label: ReactNode;
  value: ReactNode;
  strong?: boolean;
  soft?: boolean;
  lined?: boolean;
  mono?: boolean;
}) {
  const valueClass = [
    'kv__val',
    strong ? 'kv__val--strong' : '',
    soft ? 'kv__val--soft' : '',
    mono ? 'mono-num' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={`kv${lined ? ' kv--lined' : ''}`}>
      <span className="kv__key">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}

/**
 * 键值行，值是**一张缩略图**（点开看大图）。
 *
 * 为什么单独一个组件而不是给 `KV` 传个 `<img>`：`.kv__val` 靠 `text-align: right`
 * 把值推到行尾，而那条规则对 `<button>` 不管用 —— 直接塞图会紧贴标签、右边空一大块。
 * 边界（44×56 的圆角白底 + 放大角标）与身份证卡里的头像切图同高，翻页时视觉连续。
 *
 * 用在两处：报名确认页的「职业证件照」、报名详情页的手写签名。
 */
export function KVImage({
  label,
  url,
  alt,
  lined = false,
}: {
  label: ReactNode;
  url: string;
  /** 无障碍名（「放大查看 XX」会用到） */
  alt: string;
  lined?: boolean;
}) {
  return (
    <div className={`kv${lined ? ' kv--lined' : ''}`}>
      <span className="kv__key">{label}</span>
      <span className="kv__val kv__val--photo">
        <button
          type="button"
          className="zoom-hit"
          aria-label={`放大查看${alt}`}
          onClick={() => openImagePreview(url)}
        >
          <img className="kv__photo" src={url} alt={alt} />
          <span className="zoom-hit__badge">
            <SearchIcon size={10} strokeWidth={2.6} />
          </span>
        </button>
      </span>
    </div>
  );
}

/** 可点行（左标题 / 中描述 / 右附加值 + 箭头） */
export function Cell({
  title,
  description,
  extra,
  icon,
  arrow = false,
  onClick,
}: {
  title: ReactNode;
  description?: ReactNode;
  extra?: ReactNode;
  icon?: ReactNode;
  arrow?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      {icon ? <span className="cell__icon">{icon}</span> : null}
      <span className="cell__body">
        <span className="cell__title">{title}</span>
        {description !== undefined ? <span className="cell__desc">{description}</span> : null}
      </span>
      {extra !== undefined ? <span className="cell__extra">{extra}</span> : null}
      {arrow ? (
        <span className="cell__arrow">
          <ChevronRightIcon size={15} />
        </span>
      ) : null}
    </>
  );

  if (!onClick) return <div className="cell">{content}</div>;

  return (
    <button type="button" className="cell" onClick={onClick}>
      {content}
    </button>
  );
}

export function SectionLabel({ children }: { children: ReactNode }) {
  return <div className="section-label">{children}</div>;
}

/* ============================================================================
 * 行式卡片（个人中心形态：卡头 + 88 定宽标签行）
 * ========================================================================== */
export function ListCard({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <section className="list-card">
      {title !== undefined ? <div className="list-card__head">{title}</div> : null}
      {children}
    </section>
  );
}

/**
 * 一行：`onClick` → 整行可点（左侧标题 + 右箭头）；
 * 否则为「88 定宽标签 + 值 + 可选的修改动作」，两者互斥以免按钮嵌套。
 */
export function ListRow({
  label,
  value,
  actionText,
  onAction,
  onClick,
}: {
  label: string;
  value?: ReactNode;
  actionText?: string;
  onAction?: () => void;
  onClick?: () => void;
}) {
  if (onClick) {
    return (
      <button type="button" className="row-item row-item--link" onClick={onClick}>
        <span>{label}</span>
        <span className="row-item__chev">
          <ChevronRightIcon size={15} />
        </span>
      </button>
    );
  }

  return (
    <div className="row-item">
      <span className="row-item__key">{label}</span>
      {value !== undefined ? <span className="row-item__val">{value}</span> : null}
      {actionText !== undefined && onAction ? (
        <button type="button" className="row-item__act" onClick={onAction}>
          {actionText}
        </button>
      ) : null}
    </div>
  );
}

/* ============================================================================
 * 状态胶囊
 * ========================================================================== */
const STATUS_TONE: Record<RegistrationStatus, string> = {
  DRAFT: 'muted',
  SUBMITTED: 'info',
  REJECTED: 'danger',
  PENDING_PAYMENT: 'warning',
  ENROLLED: 'success',
  REFUNDED: 'violet',
  VOIDED: 'muted',
};

/**
 * 报名单状态标签。文案取自 `REGISTRATION_STATUS_LABEL`（与状态机文档一致）——
 * 界面**不得**出现「审核中 / 等待审核 / 审核通过」的表述：核验在提交前完成。
 */
export function StatusTag({
  status,
  ghost = false,
}: {
  status: RegistrationStatus;
  ghost?: boolean;
}) {
  return (
    <span className={`pill${ghost ? ' pill--ghost' : ` pill--${STATUS_TONE[status]}`}`}>
      {REGISTRATION_STATUS_LABEL[status]}
    </span>
  );
}

/**
 * 开课状态标签 —— **独立维度**（C-22）。
 * 与报名单主状态并列展示，而不是它的子状态；用「灰 / 蓝 / 绿」的强弱语义，
 * 与服务端 `CourseStatus` 一一对应。
 */
const COURSE_STATUS_TONE: Record<CourseStatus, string> = {
  NOT_STARTED: 'muted',
  IN_PROGRESS: 'info',
  FINISHED: 'success',
};

export function CourseStatusTag({ status }: { status: CourseStatus }) {
  return (
    <span className={`pill pill--${COURSE_STATUS_TONE[status]}`}>
      {COURSE_STATUS_LABEL[status]}
    </span>
  );
}

/* ============================================================================
 * 提示条
 * ========================================================================== */
/** 内联提示（浅底 + 图标），用于页面内的状态说明 */
export function Notice({
  tone = 'info',
  children,
}: {
  tone?: 'info' | 'warning' | 'danger' | 'success';
  children: ReactNode;
}) {
  return (
    <div className={`notice notice--${tone}`} role="note">
      <span className="notice__icon">
        {tone === 'info' ? (
          <InfoIcon size={14} />
        ) : tone === 'success' ? (
          <CheckIcon size={14} />
        ) : (
          <AlertIcon size={14} />
        )}
      </span>
      <span>{children}</span>
    </div>
  );
}

/** 公告条（白底卡片 + 琥珀「公告」标签，首页形态） */
export function NoticeBar({ text }: { text: string }) {
  return (
    <div className="notice-bar">
      <span className="notice-bar__tag">公告</span>
      <span className="notice-bar__text">{text}</span>
    </div>
  );
}

/* ============================================================================
 * 进度轨 / 步骤点
 * ========================================================================== */
/**
 * 「第 N / M 步」进度条。
 * 填充宽度对齐**当前步骤标签的中心**：`(2i+1) / 2n`（设计稿第 3/4 步 = 62.5%）。
 */
export function ProgressRail({
  steps,
  current,
  tip,
  tone,
}: {
  steps: readonly string[];
  /** 当前步骤下标（0 起） */
  current: number;
  tip?: string;
  /** `plain` 用于深色渐变卡内 */
  tone?: 'plain';
}) {
  const ratio =
    steps.length === 0
      ? 0
      : ((2 * Math.min(current, steps.length - 1) + 1) / (2 * steps.length)) * 100;

  return (
    <div className={`rail-block${tone === 'plain' ? ' rail-block--plain' : ''}`}>
      {tip ? <div className="rail-block__tip">{tip}</div> : null}
      <div className="rail">
        <div className="rail__fill" style={{ width: `${ratio}%` }} />
      </div>
      <div className="rail-labels">
        {steps.map((label, index) => (
          <span
            key={label}
            className={`rail-labels__item${
              index < current
                ? ' rail-labels__item--done'
                : index === current
                  ? ' rail-labels__item--current'
                  : ''
            }`}
          >
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

/**
 * 报名表单顶部的三步圆点（纯圆点：已完成实心 / 当前蓝环 / 未开始灰环，无序号）
 *
 * 2026-10-21：可点跳转。原来这里只是 `<ol>` 纯展示，学员想回去改上一个字只能靠
 * 页头那颗「返回」——它长得跟「退出页面」一样，很多人不敢点。
 *
 * 但**不是随便点**：只有 `maxReached` 及之前的格子可点。已到达过的步骤一定是校验
 * 通过过的，回去改是安全的；未到达的步骤跳过去等于绕过必填校验（比如直接跳到
 * 确认页提交一份半成品）。所以可点性由「是否到达过」决定，不由「当前是第几步」。
 */
export function StepDots({
  steps,
  current,
  maxReached = current,
  onSelect,
}: {
  steps: readonly string[];
  current: number;
  /** 已到达过的最远步骤下标（含） */
  maxReached?: number;
  /** 传了才可点；不传保持纯展示（其他页面若复用不受影响） */
  onSelect?: (index: number) => void;
}) {
  return (
    <ol className="step-dots" aria-label={`第 ${current + 1} 步，共 ${steps.length} 步`}>
      {steps.map((label, index) => {
        const dotClass = `step-dots__dot${
          index < current
            ? ' step-dots__dot--done'
            : index === current
              ? ' step-dots__dot--current'
              : ''
        }`;
        const labelClass = `step-dots__label${
          index < current
            ? ' step-dots__label--done'
            : index === current
              ? ' step-dots__label--current'
              : ''
        }`;
        const reachable = Boolean(onSelect) && index <= maxReached && index !== current;
        if (!reachable) {
          return (
            <li
              key={label}
              className="step-dots__item"
              aria-current={index === current || undefined}
            >
              <span className={dotClass} aria-hidden="true" />
              <span className={labelClass}>{label}</span>
            </li>
          );
        }
        return (
          <li key={label} className="step-dots__item">
            <button
              type="button"
              className="step-dots__hit"
              onClick={() => onSelect?.(index)}
              aria-label={`返回第 ${index + 1} 步：${label}`}
            >
              <span className={dotClass} aria-hidden="true" />
              <span className={labelClass}>{label}</span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

/* ============================================================================
 * 状态块（加载 / 空态 / 错误）
 * ========================================================================== */
export function LoadingBlock({ text = '加载中' }: { text?: string }) {
  return (
    <div className="state-block">
      <span className="loading-ring" aria-hidden="true" />
      <div className="state-block__text">{text}</div>
    </div>
  );
}

export interface EmptyHintProps {
  title: string;
  description?: string;
  actionText?: string;
  onAction?: () => void;
  icon?: ReactNode;
  /**
   * 整页空态：撑满剩余高度并垂直居中（设计稿空态页构图）。
   * 页内局部占位（如「请先选择批次」提示）**不要**开这个，否则会把后续内容顶到屏幕底部。
   */
  fill?: boolean;
  /** 页内局部占位：图标与留白收一档，避免半个屏幕都是提示 */
  compact?: boolean;
}

/** 空态：说明原因 + 下一步（不用「暂无数据」） */
export function EmptyHint({
  title,
  description,
  actionText,
  onAction,
  icon,
  fill = false,
  compact = false,
}: EmptyHintProps) {
  const cls = ['empty', fill ? 'empty--fill' : '', compact ? 'empty--compact' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <span className="empty__icon">{icon ?? <BellIcon size={34} strokeWidth={1.5} />}</span>
      <div className="empty__title">{title}</div>
      {description ? <div className="empty__text">{description}</div> : null}
      {actionText && onAction ? (
        <Button
          variant="primary"
          size={fill ? undefined : 'sm'}
          className="empty__action"
          onClick={onAction}
        >
          {actionText}
        </Button>
      ) : null}
    </div>
  );
}

export function ErrorBlock({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div className="state-block">
      <div className="state-block__title">加载失败</div>
      <div className="state-block__text">{message}</div>
      {onRetry ? (
        <Button variant="ghost" size="sm" className="state-block__action" onClick={onRetry}>
          重新加载
        </Button>
      ) : null}
    </div>
  );
}
